import { useTokenResolver } from "@hooks/useTokenResolver";
import { fetchDebugData } from "@lib/chatApi";
import type { ChatDebugData, ChatDebugMessage, TokenResolutions } from "@lib/types";
import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { renderTextWithTokens } from "./TokenSpan";

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(4)}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

type MessageType = "user" | "tool_result" | "tool_use" | "assistant" | "system";

function getMessageType(message: ChatDebugMessage): MessageType {
  if (message.role === "user" && message.tool_result) return "tool_result";
  if (message.role === "user") return "user";
  if (message.tool_use) return "tool_use";
  if (message.role === "assistant") return "assistant";
  return "system";
}

const MESSAGE_STYLES: Record<
  MessageType,
  { bg: string; border: string; badge: string; icon: string; label: string }
> = {
  user: {
    bg: "bg-blue-50 dark:bg-blue-950/30",
    border: "border-blue-200 dark:border-blue-800",
    badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300",
    icon: "\u{1F464}",
    label: "User",
  },
  tool_result: {
    bg: "bg-orange-50 dark:bg-orange-950/30",
    border: "border-orange-200 dark:border-orange-800",
    badge: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300",
    icon: "\u2713",
    label: "Tool Result",
  },
  tool_use: {
    bg: "bg-purple-50 dark:bg-purple-950/30",
    border: "border-purple-200 dark:border-purple-800",
    badge: "bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300",
    icon: "\u{1F527}",
    label: "Tool Use",
  },
  assistant: {
    bg: "bg-green-50 dark:bg-green-950/30",
    border: "border-green-200 dark:border-green-800",
    badge: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
    icon: "\u{1F916}",
    label: "Assistant",
  },
  system: {
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-800",
    badge: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
    icon: "\u26A0\uFE0F",
    label: "System",
  },
};

const MODEL_BADGES: Record<string, { label: string; className: string }> = {
  sonnet: {
    label: "Sonnet 4.6",
    className: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300",
  },
  haiku: {
    label: "Haiku 4.5",
    className: "bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-300",
  },
};

function DebugMessage({
  message,
  index,
  allExpanded,
  resolutions,
  resolveText,
  showRawTokens,
}: {
  message: ChatDebugMessage;
  index: number;
  allExpanded: boolean;
  resolutions: TokenResolutions;
  resolveText: (text: string) => string;
  showRawTokens: boolean;
}) {
  const type = getMessageType(message);
  const style = MESSAGE_STYLES[type];

  return (
    <div
      className={`${style.bg} ${style.border} border rounded-lg p-4 transition-all hover:shadow-md`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{style.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <span className={`${style.badge} px-2 py-1 rounded text-xs font-semibold`}>
                {style.label}
              </span>
              {message.model_used && MODEL_BADGES[message.model_used] && (
                <span
                  className={`${MODEL_BADGES[message.model_used].className} px-2 py-1 rounded text-xs font-semibold`}
                >
                  {MODEL_BADGES[message.model_used].label}
                </span>
              )}
              <span className="text-xs text-text-tertiary">#{index + 1}</span>
            </div>
            <div className="text-xs text-text-tertiary mt-1">
              {new Date(message.created_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Token usage badge (assistant messages with token data) */}
      {message.role === "assistant" && message.total_tokens > 0 && (
        <div className="mb-3 p-2 bg-white/50 dark:bg-black/20 rounded border border-border">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 text-xs">
              <div>
                <span className="text-text-tertiary">In:</span>{" "}
                <span className="font-semibold text-indigo-700 dark:text-indigo-400">
                  {formatNumber(message.input_tokens)}
                </span>
              </div>
              <div>
                <span className="text-text-tertiary">Out:</span>{" "}
                <span className="font-semibold text-purple-700 dark:text-purple-400">
                  {formatNumber(message.output_tokens)}
                </span>
              </div>
              {message.cache_creation_input_tokens > 0 && (
                <div>
                  <span className="text-text-tertiary">Cache Write:</span>{" "}
                  <span className="font-semibold text-orange-700 dark:text-orange-400">
                    {formatNumber(message.cache_creation_input_tokens)}
                  </span>
                </div>
              )}
              {message.cache_read_input_tokens > 0 && (
                <div>
                  <span className="text-text-tertiary">Cache Read:</span>{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {formatNumber(message.cache_read_input_tokens)}
                  </span>
                </div>
              )}
              <div className="pl-2 border-l border-border">
                <span className="text-text-tertiary">Total:</span>{" "}
                <span className="font-bold text-text-primary">
                  {formatNumber(message.total_tokens)}
                </span>
              </div>
            </div>
            <div className="text-xs">
              <span className="text-text-tertiary">Cost:</span>{" "}
              <span className="font-bold text-green-700 dark:text-green-400">
                {formatCost(message.estimated_cost_cents)}
              </span>
            </div>
          </div>
          {message.api_model && (
            <div className="mt-1 text-xs text-text-tertiary">
              Model: <span className="font-mono text-text-secondary">{message.api_model}</span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="text-sm text-text-primary whitespace-pre-wrap mb-3">
        {showRawTokens ? message.content : renderTextWithTokens(message.content, resolutions)}
      </div>

      {/* Tool Use Details */}
      {type === "tool_use" && (
        <div className="mt-3 p-3 bg-white dark:bg-black/20 rounded border border-purple-200 dark:border-purple-800">
          <div className="text-xs font-semibold text-purple-900 dark:text-purple-300 mb-2">
            Tool Request Details
          </div>
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-text-secondary">Tool:</span>{" "}
              <span className="text-xs text-text-primary font-mono">{message.tool_name}</span>
            </div>
            <div>
              <span className="text-xs font-medium text-text-secondary">Tool Use ID:</span>{" "}
              <span className="text-xs text-text-tertiary font-mono">{message.tool_use_id}</span>
            </div>
            {message.tool_input && (
              <details className="mt-2" open={allExpanded || undefined}>
                <summary className="text-xs font-medium text-text-secondary cursor-pointer hover:text-purple-700 dark:hover:text-purple-400">
                  Tool Input Parameters
                </summary>
                <pre className="mt-2 p-2 bg-surface-secondary rounded text-xs overflow-x-auto">
                  {JSON.stringify(message.tool_input, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}

      {/* Tool Result Details */}
      {type === "tool_result" && (
        <div className="mt-3 p-3 bg-white dark:bg-black/20 rounded border border-orange-200 dark:border-orange-800">
          <div className="text-xs font-semibold text-orange-900 dark:text-orange-300 mb-2">
            Tool Execution Result
          </div>
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-text-secondary">Tool Use ID:</span>{" "}
              <span className="text-xs text-text-tertiary font-mono">{message.tool_use_id}</span>
            </div>
            {message.tool_output != null && (
              <>
                <div>
                  <span className="text-xs font-medium text-text-secondary">Status:</span>{" "}
                  <span
                    className={`text-xs font-semibold ${
                      typeof message.tool_output === "object" &&
                      message.tool_output !== null &&
                      "success" in message.tool_output &&
                      !(message.tool_output as Record<string, unknown>).success
                        ? "text-red-600"
                        : "text-green-600"
                    }`}
                  >
                    {typeof message.tool_output === "object" &&
                    message.tool_output !== null &&
                    "success" in message.tool_output &&
                    !(message.tool_output as Record<string, unknown>).success
                      ? "Error"
                      : "Success"}
                  </span>
                </div>
                <details className="mt-2" open={allExpanded || undefined}>
                  <summary className="text-xs font-medium text-text-secondary cursor-pointer hover:text-orange-700 dark:hover:text-orange-400">
                    Tool Output{showRawTokens ? " (raw tokens)" : " (tokens resolved)"}
                  </summary>
                  <pre className="mt-2 p-2 bg-surface-secondary rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
                    {showRawTokens
                      ? JSON.stringify(message.tool_output, null, 2)
                      : resolveText(JSON.stringify(message.tool_output, null, 2))}
                  </pre>
                </details>
              </>
            )}
          </div>
        </div>
      )}

      {/* System Prompt */}
      {message.system_prompt && (
        <details className="mt-3" open={allExpanded || undefined}>
          <summary className="text-xs font-medium text-text-secondary cursor-pointer hover:text-indigo-700 dark:hover:text-indigo-400">
            System Prompt Used
          </summary>
          <div className="mt-2 p-3 bg-white dark:bg-black/20 rounded border border-border">
            <pre className="text-xs text-text-secondary whitespace-pre-wrap overflow-x-auto">
              {message.system_prompt}
            </pre>
          </div>
        </details>
      )}

      {/* Wire payload — what was actually sent to the LLM provider, post-redaction */}
      {message.wire_payload && (
        <WirePayloadSection payload={message.wire_payload} allExpanded={allExpanded} />
      )}

      {/* Full Metadata */}
      {message.metadata && Object.keys(message.metadata).length > 0 && (
        <details className="mt-3" open={allExpanded || undefined}>
          <summary className="text-xs font-medium text-text-tertiary cursor-pointer hover:text-text-secondary">
            Full Metadata
          </summary>
          <pre className="mt-2 p-2 bg-surface-secondary rounded text-xs overflow-x-auto">
            {JSON.stringify(message.metadata, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function WirePayloadSection({
  payload,
  allExpanded,
}: {
  payload: NonNullable<ChatDebugMessage["wire_payload"]>;
  allExpanded: boolean;
}) {
  const candidateRedacted = payload.redaction_counts.candidate ?? 0;
  const genericRedacted = payload.redaction_counts.generic ?? 0;
  const suspectedCandidate = payload.suspected_pii_findings.candidate ?? 0;
  const suspectedGeneric = payload.suspected_pii_findings.generic ?? 0;
  const suspectedTotal = suspectedCandidate + suspectedGeneric;
  const flagged = !payload.clean;

  return (
    <details className="mt-3" open={flagged || allExpanded || undefined}>
      <summary
        className={`text-xs font-semibold cursor-pointer flex items-center gap-2 ${
          flagged ? "text-red-700 dark:text-red-300" : "text-text-secondary hover:text-text-primary"
        }`}
      >
        Wire payload
        <span
          className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${
            flagged
              ? "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300"
              : "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300"
          }`}
          title={
            flagged
              ? `Suspected PII labels: ${(payload.suspected_pii_findings.generic_labels || []).join(", ") || "(candidate names)"}`
              : "No suspected PII detected"
          }
        >
          {flagged ? `${suspectedTotal} suspected PII` : "Clean"}
        </span>
        <span className="text-text-tertiary font-normal">
          {payload.provider}/{payload.model} · redacted {candidateRedacted} candidate /{" "}
          {genericRedacted} generic
        </span>
      </summary>
      {flagged && (payload.suspected_pii_findings.generic_labels?.length ?? 0) > 0 && (
        <div className="mt-2 p-2 rounded border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 text-xs text-red-800 dark:text-red-300">
          <div className="font-semibold mb-1">Suspected leak labels:</div>
          <div className="font-mono">
            {(payload.suspected_pii_findings.generic_labels || []).join(", ")}
          </div>
        </div>
      )}
      <div className="mt-2 grid grid-cols-1 gap-2">
        <details open={flagged || allExpanded || undefined}>
          <summary className="text-xs font-medium text-text-secondary cursor-pointer">
            System prompt sent
          </summary>
          <pre className="mt-1 p-2 bg-surface-secondary rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
            {JSON.stringify(payload.system, null, 2)}
          </pre>
        </details>
        <details open={flagged || allExpanded || undefined}>
          <summary className="text-xs font-medium text-text-secondary cursor-pointer">
            Messages sent
          </summary>
          <pre className="mt-1 p-2 bg-surface-secondary rounded text-xs overflow-x-auto max-h-96 overflow-y-auto">
            {JSON.stringify(payload.messages, null, 2)}
          </pre>
        </details>
        <details open={allExpanded || undefined}>
          <summary className="text-xs font-medium text-text-secondary cursor-pointer">
            Tools sent
          </summary>
          <pre className="mt-1 p-2 bg-surface-secondary rounded text-xs overflow-x-auto max-h-64 overflow-y-auto">
            {JSON.stringify(payload.tools, null, 2)}
          </pre>
        </details>
      </div>
    </details>
  );
}

export function ThreadDebug() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ChatDebugData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [showRawTokens, setShowRawTokens] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { resolutions, resolveText } = useTokenResolver(id);

  useEffect(() => {
    if (!id) return;
    fetchDebugData(id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [id]);

  const toggleAllDetails = useCallback(() => {
    if (containerRef.current) {
      const details = containerRef.current.querySelectorAll("details");
      const newState = !allExpanded;
      for (const el of details) {
        el.open = newState;
      }
      setAllExpanded(newState);
    }
  }, [allExpanded]);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-300">
          Error loading debug data: {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 size={24} className="animate-spin text-text-tertiary" />
      </div>
    );
  }

  const { thread, statistics, tokens, cost, messages } = data;

  const modelCounts = messages.reduce<Record<string, number>>((acc, m) => {
    if (m.model_used) acc[m.model_used] = (acc[m.model_used] || 0) + 1;
    return acc;
  }, {});
  const modelBreakdown = Object.entries(modelCounts).sort((a, b) => b[1] - a[1]);
  const warningThreshold = Math.floor(thread.max_turns_limit * 0.8);

  const statusColors: Record<string, string> = {
    active: "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300",
    completed: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
    error: "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300",
    interrupted: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300",
  };

  return (
    <div ref={containerRef} className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-text-secondary hover:text-text-primary"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold text-text-primary">Debug View: {thread.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowRawTokens((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-text-secondary bg-card rounded border border-border hover:bg-card-hover transition-colors"
            title="Persisted content carries PII tokens (C5, [EMAIL_1]); toggle between resolved names and the raw tokens"
          >
            {showRawTokens ? "Show Resolved Tokens" : "Show Raw Tokens"}
          </button>
          <button
            type="button"
            onClick={toggleAllDetails}
            className="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium text-text-secondary bg-card rounded border border-border hover:bg-card-hover transition-colors"
          >
            <ChevronDown
              size={16}
              className={`transition-transform ${allExpanded ? "rotate-180" : ""}`}
            />
            {allExpanded ? "Collapse All Details" : "Expand All Details"}
          </button>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[thread.status] || statusColors.active}`}
          >
            {thread.status}
          </span>
        </div>
      </div>

      {/* Thread Statistics */}
      <div className="bg-surface-secondary rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold text-text-secondary mb-3">Thread Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Stat label="Total Messages" value={statistics.total_messages} />
          <Stat label="User Messages" value={statistics.user_messages} color="text-blue-600" />
          <Stat
            label="Assistant Messages"
            value={statistics.assistant_messages}
            color="text-green-600"
          />
          <Stat
            label="Current Turn Count"
            value={`${thread.current_turn_count} / ${thread.max_turns_limit}`}
            color="text-purple-600"
          />
          {modelBreakdown.map(([model, count]) => (
            <Stat key={model} label={`${model} Messages`} value={count} color="text-indigo-600" />
          ))}
        </div>
        {thread.current_turn_count >= warningThreshold && (
          <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-300 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-yellow-600">&#9888;</span>
              <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                Warning: {thread.current_turn_count} turns — approaching MAX_TURNS limit (
                {thread.max_turns_limit})
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Token Usage & Cost */}
      {tokens.total_tokens > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800 p-4">
          <h2 className="text-sm font-semibold text-text-secondary mb-3">
            Token Usage &amp; Estimated Cost
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat
              label="Input Tokens"
              value={formatNumber(tokens.total_input_tokens)}
              color="text-indigo-600"
            />
            <Stat
              label="Output Tokens"
              value={formatNumber(tokens.total_output_tokens)}
              color="text-purple-600"
            />
            <Stat
              label="Cache Write Tokens"
              value={formatNumber(tokens.total_cache_creation_tokens)}
              color="text-orange-600"
            />
            <Stat
              label="Cache Read Tokens"
              value={formatNumber(tokens.total_cache_read_tokens)}
              color="text-green-600"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-indigo-200 dark:border-indigo-800">
            <Stat label="Total Tokens" value={formatNumber(tokens.total_tokens)} large />
            <Stat
              label="Estimated Cost"
              value={formatCost(cost.estimated_cost_cents)}
              color="text-green-700"
              large
            />
            <Stat
              label="Cache Efficiency"
              value={`${cost.cache_efficiency_percent.toFixed(1)}%`}
              color={cost.cache_efficiency_percent > 0 ? "text-green-600" : "text-text-tertiary"}
              large
            />
          </div>
          {cost.cache_savings_cents > 0 && (
            <div className="mt-4 p-3 bg-white/70 dark:bg-black/20 rounded border border-green-300 dark:border-green-800 text-sm">
              <span className="font-semibold text-green-800 dark:text-green-300">
                Cache Savings:{" "}
              </span>
              <span className="text-text-secondary">
                {formatCost(cost.cache_savings_cents)} (Cost without caching:{" "}
                {formatCost(cost.estimated_cost_without_caching_cents)})
              </span>
            </div>
          )}
        </div>
      )}

      {/* Message Flow */}
      <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
        <div className="p-4">
          <h2 className="text-sm font-semibold text-text-secondary mb-4">Message Flow</h2>
          <div className="space-y-4">
            {messages.map((message, index) => (
              <DebugMessage
                key={message.id}
                message={message}
                index={index}
                allExpanded={allExpanded}
                resolutions={resolutions}
                resolveText={resolveText}
                showRawTokens={showRawTokens}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  color,
  large,
}: {
  label: string;
  value: string | number;
  color?: string;
  large?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-text-tertiary">{label}</div>
      <div
        className={`${large ? "text-2xl" : "text-lg"} font-bold ${color || "text-text-primary"}`}
      >
        {value}
      </div>
    </div>
  );
}
